import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import {
  approveAdjustment,
  cancelAdjustment,
  createAdjustment,
  fetchAdjustments,
  fetchReasonCodes,
  submitAdjustment,
} from '../api'
import type { CreateAdjustmentBody, ReasonCode } from '../types'
import { inventoryKey } from './use-inventory'

const adjustmentsKey = [...inventoryKey, 'adjustments'] as const

/** One page of adjustments, newest first, for the Adjustments tab. */
export function useAdjustmentList(page: number, pageSize: number) {
  const query = { limit: pageSize, offset: page * pageSize }

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...adjustmentsKey, 'list', query],
    queryFn: () => fetchAdjustments(query),
    placeholderData: (previous) => previous,
  })

  return {
    adjustments: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * Every reason the business can see, system ones included, by id — for naming
 * the reason on a line or a movement. A picker filters out `system` itself.
 */
export function useReasonCodes() {
  const query = { limit: PAGE_LIMIT_MAX, offset: 0, includeSystem: true }

  const { data, isPending, error } = useQuery({
    queryKey: [...inventoryKey, 'reason-codes', query],
    queryFn: () => fetchReasonCodes(query),
    staleTime: 5 * 60_000,
  })

  const reasons = useMemo(() => data?.rows ?? [], [data])
  const byId = useMemo(
    () => new Map<string, ReasonCode>(reasons.map((reason) => [reason.id, reason])),
    [reasons],
  )

  return { reasons, byId, isLoading: isPending, error }
}

/** Posting moves stock: the items' totals, the per-location levels and the ledger all change. */
export function invalidateStock(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'items'] }),
    queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'item'] }),
    queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'stock'] }),
    queryClient.invalidateQueries({ queryKey: [...inventoryKey, 'movements'] }),
  ])
}

export function useAdjustmentActions() {
  const queryClient = useQueryClient()

  /* A draft moves no stock, so only the adjustments are refreshed. */
  const create = useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateAdjustmentBody
      idempotencyKey: string
    }) => createAdjustment(body, idempotencyKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adjustmentsKey }),
  })

  /* Submit and approve may post, so the stock is refreshed with the list. */
  const afterStep = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: adjustmentsKey }),
      invalidateStock(queryClient),
    ])

  const submit = useMutation({
    mutationFn: ({ id, idempotencyKey }: { id: string; idempotencyKey: string }) =>
      submitAdjustment(id, idempotencyKey),
    onSuccess: afterStep,
  })

  const approve = useMutation({
    mutationFn: ({ id, idempotencyKey }: { id: string; idempotencyKey: string }) =>
      approveAdjustment(id, idempotencyKey),
    onSuccess: afterStep,
  })

  const cancel = useMutation({
    mutationFn: ({ id, idempotencyKey }: { id: string; idempotencyKey: string }) =>
      cancelAdjustment(id, idempotencyKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adjustmentsKey }),
  })

  return { create, submit, approve, cancel }
}
